import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface ProgressBarProps {
  /** Progress value 0-100. If undefined, shows indeterminate animation */
  value?: number;
  /** Height of the progress bar in pixels (default: 100 to match OscillatingDotsLoader) */
  height?: number;
  /** Show label with percentage */
  showLabel?: boolean;
}

/**
 * Unified progress bar with oscillating dots animation.
 * Uses exact same animation as OscillatingDotsLoader but fills horizontally based on progress.
 */
export default function ProgressBar({ value, height = 100, showLabel = false }: ProgressBarProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const theme = useTheme();
  const isDeterminate = value !== undefined;
  const progress = Math.max(0, Math.min(100, value ?? 0));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions
    const containerWidth = canvas.offsetWidth || 300;
    canvas.width = containerWidth;
    canvas.height = height;

    let time = 0;
    let lastTime = 0;

    // Get colors from theme
    const primaryColor = theme.palette.primary.main;
    const bgColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
    
    // Convert hex to RGB
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 65, g: 143, b: 222 };
    };

    const primaryRgb = hexToRgb(primaryColor);

    // EXACT same parameters as OscillatingDotsLoader
    const dotCount = Math.floor(containerWidth / 12);
    const rowCount = 4;
    const spacing = containerWidth / (dotCount + 1);
    const maxAmplitude = 8 + (rowCount - 1) * 2;
    const padding = maxAmplitude + 5;
    const availableHeight = height - (padding * 2);
    const rowSpacing = availableHeight / (rowCount - 1);

    const animate = (timestamp: number) => {
      if (!lastTime) lastTime = timestamp;
      const deltaTime = timestamp - lastTime;
      lastTime = timestamp;
      time += deltaTime * 0.001;

      ctx.clearRect(0, 0, containerWidth, height);

      // Draw subtle background track for determinate mode
      if (isDeterminate) {
        ctx.fillStyle = bgColor;
        ctx.beginPath();
        ctx.roundRect(0, 0, containerWidth, height, 8);
        ctx.fill();
      }

      // Calculate fill width based on progress
      const fillWidth = isDeterminate 
        ? (progress / 100) * containerWidth 
        : containerWidth;

      // Draw oscillating dots (exactly like OscillatingDotsLoader)
      for (let row = 0; row < rowCount; row++) {
        const y = padding + rowSpacing * row;
        
        for (let i = 0; i < dotCount; i++) {
          const x = spacing * (i + 1);
          
          // Only draw dots within the fill region (for determinate mode)
          if (isDeterminate && x > fillWidth) continue;
          
          // Wave parameters vary by row (exact same as original)
          const amplitude = 8 + row * 2;
          const frequency = 1 + row * 0.2;
          const phaseOffset = row * 0.5;
          
          // Calculate vertical offset
          const offset = Math.sin(time * frequency + i * 0.2 + phaseOffset) * amplitude;
          const finalY = y + offset;
          
          // Color intensity based on position and time
          const intensity = 0.6 + Math.sin(time * 2 + i * 0.1 + row * 0.3) * 0.4;
          
          // Fade dots near the edge for smooth fill effect (determinate only)
          let finalIntensity = intensity;
          if (isDeterminate && fillWidth > 0) {
            const distanceFromEdge = fillWidth - x;
            if (distanceFromEdge < 20 && distanceFromEdge > 0) {
              finalIntensity *= distanceFromEdge / 20;
            }
          }
          
          // Draw dot with primary color
          ctx.beginPath();
          ctx.arc(x, finalY, 2.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, ${finalIntensity})`;
          ctx.fill();
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, height, theme.palette.primary.main, theme.palette.mode, isDeterminate, progress]);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: `${height}px`,
        }}
      />
      {showLabel && isDeterminate && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            {Math.round(progress)}%
          </Typography>
        </Box>
      )}
    </Box>
  );
}
